import Vue from "vue";
import Vuex from "vuex";
import "firebase/database";
import "firebase/firestore";
import "firebase/auth";
import firebase from "firebase/app";
import router from "../router";

Vue.use(Vuex);

// Kept outside the store so a game switch can detach the previous subscription
// instead of leaving two listeners writing to `cards`.
let cardsRef = null;

export default new Vuex.Store({
  state: {
    cards: [],
    user: {},
    currentGame: {
      status: "",
      schedule: new Date(),
      uid: ""
    },
    sheets: [],
    info: {}
  },
  mutations: {
    SET_GAME_ID(state, payload) {
      state.currentGame = payload;
    },
    SET_GAME_CARD(state, payload) {
      state.cards = payload;
    },
    SET_GAME_STATUS(state, payload) {
      state.currentGame.status = payload;
    },
    SET_USER(state, payload) {
      state.user = payload;
    },
    SET_INFO(state, payload) {
      state.info = payload;
    },
    SET_GAME_SHEET(state, payload) {
      state.sheets = payload;
    }
  },
  actions: {
    // eslint-disable-next-line no-empty-pattern
    login({}, form) {
      return firebase.auth().signInWithEmailAndPassword(form.email, form.password);
    },
    async logout() {
      await firebase.auth().signOut();
      router.push("/login");
    },
    forgot(unused, email) {
      return firebase.auth().sendPasswordResetEmail(email);
    },
    // Subscribes to a game's tickets and resolves once the first payload has
    // landed, so callers can await an initial render.
    fetchCards({ commit }, gameId) {
      if (cardsRef) {
        cardsRef.off();
      }
      cardsRef = firebase.database().ref("games").child(gameId);

      return new Promise((resolve) => {
        cardsRef.on("value", (snap) => {
          const data = snap.val();
          const cards = [];

          if (data && data.cards) {
            for (const [key, value] of Object.entries(data.cards)) {
              cards.push({ ...value, phone: null, number: key.padStart(3, "0") });
            }
          }

          commit("SET_GAME_CARD", cards);
          if (data && data.data && data.data.sheetUsers) {
            commit("SET_GAME_SHEET", data.data.sheetUsers);
          }
          // Resolve even for an empty game, otherwise the initial load hangs
          // on the splash screen forever.
          resolve(cards);
        });
      });
    },
    async fetchStatus({ commit, dispatch }) {
      const dataSnap = await firebase
        .firestore()
        .collection("games")
        .where("status", "in", ["live", "schedule", "winner_declared"])
        .orderBy("schedule", "desc")
        .get();

      if (dataSnap.empty) {
        return "nogame";
      }

      const games = dataSnap.docs;
      const live = games.find((game) => game.data().status === "live");
      const scheduled = games.find((game) => game.data().status === "schedule");
      // Falls back to the most recent game so players can still review the
      // board of a game that has already finished.
      const game = live || scheduled || games[0];

      commit("SET_GAME_ID", { ...game.data(), uid: game.id });
      await dispatch("fetchCards", game.id);

      return game === scheduled ? "/" : "live";
    },
    async fetchInfo({ commit }) {
      const snap = await firebase.database().ref("info").once("value");
      if (snap.exists()) {
        commit("SET_INFO", snap.val());
      }
      return snap;
    }
  },
  modules: {}
});
